import { inject } from "mobx-react";
import { useMemo } from "react";
import * as React from "react";
import { useHistory } from "react-router";

import { COAST_CATEGORIES, COAST_CATEGORY_PLACEHOLDER } from "@inferno/renderer-shared-core";
import { CoastFeedCategorySelector } from "./CoastFeedCategorySelector.component";
import { CoastFeedDateSelector } from "./CoastFeedDateSelector.component";
import "./CoastFeedNavigation.style.scss";
import type { Store } from "@inferno/renderer-shared-core";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { buildGuestsPath } from "../../../lib/coastFeed";
import { getCoastHosts, getCoastTopics } from "../../../../../core/services/Coast";

interface CombinedHostTopic {
  id?: string;
  name?: string;
  slug?: string | null;
}

interface CoastFeedNavigationProps {
  displayHostCategories?: boolean;
  displayDateSelect?: boolean;
  store?: Store;
  children?: React.ReactNode;
}

const getStartYear = (isInTheNewsPage: boolean, isGuestsPage: boolean) => {
  if (isInTheNewsPage) {
    return 2009;
  }

  if (isGuestsPage) {
    return 2008;
  }

  return 2003;
};
const sortHostsByCustomOrder = (hosts: Array<CombinedHostTopic | null>) =>
  hosts.sort((a, b) => {
    const getIndex = (name: string) => {
      const index = COAST_CATEGORIES.customHostOrder.indexOf(name);
      return index === -1 ? 9999 : index;
    };
    return getIndex(a?.name ?? "") - getIndex(b?.name ?? "");
  });

const transformToDynamicItem = (item: CombinedHostTopic) => ({
  name: String(item.slug || item.id).replace("coast-categories/", ""),
  displayName: item.name || "",
});

export const CoastFeedNavigation = inject("store")(
  ({ store, displayHostCategories = true, displayDateSelect = true, children }: CoastFeedNavigationProps) => {
    if (!store) {
      return null;
    }
    const loader = async () => {
      const [hostItems, topicItems] = await Promise.all([getCoastHosts(), getCoastTopics()]);

      const categoryNames = new Set();
      const topicHostNames = new Set();
      const transformedTopicHosts: Array<CombinedHostTopic> = [];
      const dedupApiHosts: Array<CombinedHostTopic | null> = [];
      const allTopicItems: Array<CombinedHostTopic> = [];

      topicItems.forEach(item => {
        const topicItem = { ...item };
        if (topicItem?.id?.includes("photo-of-the-day-2009")) topicItem.isCategory = true;
        const nameLower = topicItem?.name?.toLowerCase() ?? "";

        if (topicItem?.isCategory) {
          categoryNames.add(nameLower);
          allTopicItems.push(topicItem);
        } else if (!categoryNames.has(nameLower) && !topicHostNames.has(nameLower)) {
          transformedTopicHosts.push({
            id: topicItem?.id ?? "",
            name: topicItem?.name ?? "",
            slug: topicItem?.id ?? "",
          });
          topicHostNames.add(nameLower);
        }
      });

      hostItems.forEach(host => {
        if (!topicHostNames.has(host?.name?.toLowerCase() ?? "")) {
          dedupApiHosts.push(host);
        }
      });

      return { hosts: [...transformedTopicHosts, ...dedupApiHosts], topics: allTopicItems };
    };
    const history = useHistory();

    const { routeParams, request } = store;
    const curPath = request.path;

    const selectedCategory = routeParams.coastCategory;
    const selectedMonth = routeParams.month;
    const selectedYear = routeParams.year;

    const isInTheNewsPage = useMemo(() => curPath.startsWith("/inthenews/"), [curPath]);
    const isGuestsPage = useMemo(() => curPath.startsWith("/guests/"), [curPath]);
    const startYear = useMemo(() => getStartYear(isInTheNewsPage, isGuestsPage), [isGuestsPage, isInTheNewsPage]);

    const handleChange = useMemo(
      () => (e: React.FormEvent<HTMLSelectElement>) => {
        const property = e.currentTarget.name;
        const value = e.currentTarget.value;

        let category = selectedCategory;
        let month = Number(selectedMonth) || null;
        let year = Number(selectedYear) || null;

        switch (property) {
          case "year":
            year = Number(value);
            break;
          case "month":
            month = Number(value);
            break;
          case "category":
            category = value;
            break;
        }

        if (isGuestsPage) {
          const guestPath = buildGuestsPath({
            slug: routeParams.slug,
            category: category || undefined,
            year: year || undefined,
            month: month || undefined,
          });
          history.push(guestPath);
          return;
        }

        const [, basePath] = /^(\/[^\/]*\/)/.exec(curPath) || [null, ""];
        let path = basePath || "/";

        if (!isInTheNewsPage && ((!!category && category !== COAST_CATEGORY_PLACEHOLDER) || year)) {
          path += `${category || COAST_CATEGORY_PLACEHOLDER}/`;
        }

        if (year) {
          path += `${year}/`;

          if (month) {
            path += `${month}/`;
          }
        }

        history.push(path);
      },
      [
        selectedCategory,
        selectedMonth,
        selectedYear,
        curPath,
        isInTheNewsPage,
        history,
        isGuestsPage,
        routeParams.slug,
      ],
    );

    return (
      <Remote loader={loader} cacheKey="coast-feed-navigation">
        {({ data }) => {
          const hosts = data?.hosts ?? [];
          const topics = data?.topics ?? [];

          const sortedHosts = sortHostsByCustomOrder(hosts);

          const dynamicHosts = sortedHosts
            .filter((host): host is CombinedHostTopic => host !== null)
            .map(host => transformToDynamicItem(host));

          const dynamicTopics = topics.map(topic => transformToDynamicItem(topic));

          return (
            <div className="component-coast-feed-navigation">
              {displayDateSelect && (
                <CoastFeedDateSelector
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  handleChange={handleChange}
                  yearsStartFrom={startYear}
                />
              )}

              {!isInTheNewsPage && (
                <CoastFeedCategorySelector
                  displayHostCategories={!!displayHostCategories}
                  selectedCategory={selectedCategory}
                  handleChange={handleChange}
                  dynamicHosts={dynamicHosts}
                  dynamicTopics={dynamicTopics}
                />
              )}
              {children}
            </div>
          );
        }}
      </Remote>
    );
  },
);
